/**
 * Zákaznická cesta grafem — kroky navazující na primární operaci.
 *
 * Nahrazuje ``ChainOperationsSection``. Věcně jde o totéž místo v
 * konfigurátoru, ale koncept je jiný: ne „seznam přílepků k položce",
 * nýbrž **cesta**, kterou si zákazník skládá po větvích veřejného grafu
 * tenanta (spec_operations_graph § O5/1). Nabídku dalších kroků razí
 * server přes ``chain-continuations``; klient si žádné hrany nedrží.
 *
 * Mimo ``DynamicPrimaryConfigurator`` schválně: primární karta je o
 * konfiguraci JEDNÉ linky, tohle je seznam samostatně persistovaných
 * kroků s vlastními mutacemi. Slití obojího je způsob, jakým předchozí
 * konfigurátor narostl na 1600 řádků.
 *
 * Každá mutace dá přednost ``persistence`` adaptéru volajícího před
 * košíkovými hooky. To není zdvořilost: admin quote builder adresuje
 * draft objednávku přes ``?quote_id=cq_…`` a projektová stránka má
 * vlastní endpointy — sáhnout tam po košíkových hoocích znamená
 * zmutovat anonymní session košík, tedy úplně jinou položku.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { useQuote } from "@/api/queries";
import {
  useAddOperation,
  useChainContinuations,
  useProcesses,
  useRemoveOperation,
  useUpdateOperationConfig,
} from "@/api/queries/processes";
import { OperationPathPicker } from "@/components/OperationPathPicker";
import { ApiError } from "@/lib/api";
import { findModule, hasDynamicSchema } from "@/processes/registry";

import { defaultsFromFields } from "./useCustomerConfiguratorState";

import type {
  AddOperationRequest,
  CartOperation,
  ChainContinuation,
  ItemOperation,
  PathOperationInput,
  Process,
} from "@/api/types";

/** Ta část persistence adaptéru volajícího, která umí kroky cesty.
 *  Každý člen je volitelný — volající, který zná jen přidávání
 *  košíkových LINEK, prostě dostane na kroky košíkové hooky. */
export interface OperationPathPersistence {
  addOperation?: {
    isPending: boolean;
    mutateAsync: (payload: AddOperationRequest) => Promise<ItemOperation>;
  };
  updateOperationConfig?: {
    mutate: (
      variables: { operationId: number; config: Record<string, unknown> },
      options?: { onSuccess?: (data: ItemOperation) => void },
    ) => void;
  };
  removeOperation?: {
    mutate: (operationId: number, options?: { onSuccess?: () => void }) => void;
  };
  continuations?: { isFetched: boolean; data?: ChainContinuation[] };
  /** Kroky, které položka už nese.
   *
   * Typ je schválně ``CartOperation`` — **užší** tvar z košíkového
   * payloadu, ze kterého hydratace doopravdy čte. ``ItemOperation`` má
   * navíc ``fulfillment_partner_id`` a ``template_node_id``, které
   * ``GET /cart`` nevrací; deklarovat je tady znamenalo tvrdit o datech
   * něco, co v nich není. Normalizace níž s tím počítá už dřív,
   * jen to typ nepřiznával. ``ItemOperation`` je podtyp, takže volající,
   * kteří ho posílají (``MyProjectItemConfigure``), se nemění. */
  hydratedOperations?: CartOperation[];
}

interface Props {
  itemSlug: string;
  /** ``process_type`` primární operace — první krok cesty. Slouží jen
   *  k popisku „cesta zatím", samotný krok persistuje primární karta. */
  primaryProcessType?: string;
  /** Identita + rozměry primární karty, kaskádované do konfigurace
   *  každého kroku. Povinné proto, že ``config_snapshot`` primární
   *  operace se serverem razítkuje až při vložení do košíku — bez
   *  tohohle cení strategie krok proti ničemu a vrátí nulu. */
  cascadeConfig: Record<string, unknown>;
  lang: string;
  /** Cesta za primárkou, hlášená nahoru, aby ji volající poslal
   *  serveru k nacenění. Klient ceny NESČÍTÁ (KAN-121) — headline cena
   *  je ta, kterou vrátí ``/pricing/line`` pro celou položku. */
  onPathChange?: (path: PathOperationInput[]) => void;
  persistence?: OperationPathPersistence;
}

interface PathStepDraft {
  operation: ItemOperation;
  config: Record<string, unknown>;
}

export function OperationPathSection({
  itemSlug,
  primaryProcessType,
  cascadeConfig,
  lang,
  onPathChange,
  persistence,
}: Props): JSX.Element | null {
  const { t } = useTranslation("quote");
  const [pathSteps, setPathSteps] = useState<PathStepDraft[]>([]);

  const processes = useProcesses({ includeNonEntry: true });
  const processMap = useMemo(() => {
    const map = new Map<string, Process>();
    for (const p of processes.data ?? []) map.set(p.name, p);
    return map;
  }, [processes.data]);

  // Hydratace z toho, do čeho volající persistuje. Bez ní položka, která
  // už kroky nese (zákazník přidal povrchovku, odešel, vrátil se),
  // vykreslí prázdnou cestu, zatímco konec cesty je na serveru dávno za
  // primárkou — takže nabídka pokračování nevrátí nic a CTA se schová.
  //
  // KAN-414 — zdrojem je ČTENÍ POLOŽKY (``GET /quotes/{slug}``), ne
  // košík. Do KAN-414 to byl košík a fungovalo to jen proto, že
  // nahraný soubor byl v košíku od vteřiny uploadu. Nepotvrzená
  // položka tam od KAN-414 není, takže hydratace z košíku by
  // zákazníkovi po návratu vykreslila prázdnou cestu.
  const quote = useQuote(
    persistence?.hydratedOperations === undefined ? itemSlug : null,
  );
  const hasHydratedRef = useRef(false);
  useEffect(() => {
    if (hasHydratedRef.current) return;
    const operations =
      persistence?.hydratedOperations ?? quote.data?.operations;
    if (!operations) return;
    hasHydratedRef.current = true;
    const drafts = operations
      .filter((op) => op.order_index > 0)
      .sort((a, b) => a.order_index - b.order_index)
      // Tvar operace v košíkovém payloadu je užší než v mutaci, takže
      // normalizovat, ne přetypovat — chybějící členy nemají pro
      // operaci, kterou jsme právě nevytvořili, žádný význam.
      .map((op) => ({
        operation: {
          id: op.id,
          process_type: op.process_type,
          order_index: op.order_index,
          // Košíkový tvar vozí cenu za kus jako ``unit_price_czk``,
          // odpověď ``GET /quotes/{slug}`` (KAN-414) jako měnově
          // neutrální ``unit_price``. Obojí jsou MAJOR jednotky téže
          // měny, takže se jen vybírá, který člen tenhle zdroj má.
          unit_price_czk: "unit_price" in op ? op.unit_price : op.unit_price_czk,
          config_snapshot: op.config_snapshot,
          fulfillment_partner_id: null,
          estimated_duration_seconds: op.estimated_duration_seconds ?? 0,
        } as ItemOperation,
        config: (op.config_snapshot as Record<string, unknown>) ?? {},
      }));
    if (drafts.length > 0) setPathSteps(drafts);
  }, [quote.data, itemSlug, persistence?.hydratedOperations]);

  const cartContinuations = useChainContinuations(itemSlug, {
    enabled: persistence?.continuations === undefined,
  });
  const continuations = persistence?.continuations ?? cartContinuations;

  const cartAddOp = useAddOperation(itemSlug);
  const cartUpdateOpConfig = useUpdateOperationConfig(itemSlug);
  const cartRemoveOp = useRemoveOperation(itemSlug);
  const updateOpConfig = persistence?.updateOperationConfig ?? cartUpdateOpConfig;
  const removeOp = persistence?.removeOperation ?? cartRemoveOp;

  // Cesta pro cenový náhled. Do KAN-121 tady byl součet
  // ``unit_price_czk`` jednotlivých kroků — klientská aritmetika nad
  // serverovými čísly, kvůli které se cena položky nedala se serverem
  // porovnat a každá změna cenění se implementovala dvakrát.
  const pathForPricing = useMemo<PathOperationInput[]>(
    () =>
      pathSteps.map((d) => ({
        process_type: d.operation.process_type,
        config: d.config,
      })),
    [pathSteps],
  );
  useEffect(() => {
    onPathChange?.(pathForPricing);
  }, [pathForPricing, onPathChange]);

  // Kroky, které na cestě už stojí — primárka plus dosud přidané.
  // Picker je ukáže nad nabídkou, aby bylo vidět, odkud se větví.
  const pathSoFar = useMemo(() => {
    const labelFor = (processType: string): string =>
      processMap.get(processType)?.display_name ?? processType;
    const primary = primaryProcessType ? [labelFor(primaryProcessType)] : [];
    return [
      ...primary,
      ...pathSteps.map((d) => labelFor(d.operation.process_type)),
    ];
  }, [primaryProcessType, pathSteps, processMap]);

  // KAN-1025 — odmítnutí serveru musí dojít k zákazníkovi. Kód chyby
  // razí server (``pricing_unavailable`` u uzlu bez tabulky sazeb,
  // ``incompatible_configuration`` u nenavazující konfigurace); klíč,
  // který v bundlu není, spadne na obecnou větu — nikdy na prázdno.
  const formatAddStepError = useCallback(
    (err: unknown): string => {
      if (err instanceof ApiError) {
        const specific = t(`config.addPathStepError.${err.code}`, {
          defaultValue: "",
          ...err.params,
        });
        if (specific) return specific;
      }
      return t("config.addPathStepError.fallback");
    },
    [t],
  );

  const onStepAdded = (op: ItemOperation) => {
    // KAN-2195 — výchozí hodnoty deklaruje schéma operace. Bez nich formulář
    // ukazoval „Vyberte…" a cena spadla na neúplný vzorec. Krok vznikl jen
    // s konfigurací primárky, proto je server musí dostat i tady.
    const schemaDefaults = defaultsFromFields(
      processMap.get(op.process_type)?.configurator_schema,
    );
    const defaultConfig = {
      ...(findModule(processes.data, op.process_type)?.defaultConfig ?? {}),
      ...schemaDefaults,
    };
    setPathSteps((prev) => [...prev, { operation: op, config: defaultConfig }]);
    if (Object.keys(schemaDefaults).length > 0) {
      updateStepConfig(op.id, defaultConfig);
    }
  };

  const updateStepConfig = (opId: number, next: Record<string, unknown>) => {
    setPathSteps((prev) =>
      prev.map((d) => (d.operation.id === opId ? { ...d, config: next } : d)),
    );
    updateOpConfig.mutate(
      { operationId: opId, config: { ...cascadeConfig, ...next } },
      {
        // Odpověď nese přeceněný krok, takže chip u kroku i headline
        // total se pohnou na témž renderu jako volba.
        onSuccess: (data: ItemOperation) =>
          setPathSteps((prev) =>
            prev.map((d) =>
              d.operation.id === data.id ? { ...d, operation: data } : d,
            ),
          ),
      },
    );
  };

  const removeStep = (opId: number) => {
    removeOp.mutate(opId, {
      onSuccess: () =>
        setPathSteps((prev) => prev.filter((d) => d.operation.id !== opId)),
    });
  };

  // Graf je jediný zdroj pravdy: CTA se ukáže, teprve až dotaz potvrdí
  // aspoň jedno dosažitelné pokračování. KAN-2056 — i při úpravě položky
  // z košíku: nabídka pokračuje za posledním krokem, který položka už
  // nese (razí ji server), takže krok zdvojit nejde. Do té doby zákazník,
  // který díl vložil do košíku, už žádnou povrchovku přidat nemohl.
  const showAddCta =
    continuations.isFetched && (continuations.data?.length ?? 0) > 0;

  if (pathSteps.length === 0 && !showAddCta) return null;

  return (
    <div className="space-y-3" data-testid="operation-path">
      {pathSteps.map(({ operation, config }) => {
        const meta = processMap.get(operation.process_type);
        const Configurator =
          findModule(processes.data, operation.process_type)?.Configurator ?? null;
        const dynamicSchemaProps =
          meta && hasDynamicSchema(meta)
            ? {
                schema: meta.configurator_schema,
                // KAN-1011 — a secondary file input (artwork) is stored on THIS
                // operation, so the generic configurator needs to know which one.
                operationId: operation.id,
                itemSlug,
                // KAN-1401 — hodnoty, které kroku dodal předchůdce.
                suppliedConfig: operation.config_snapshot,
              }
            : {};
        return (
          <section
            key={operation.id}
            className="space-y-3 rounded-md border border-border bg-surface p-4"
            data-testid={`path-step-row-${operation.id}`}
          >
            <header className="flex items-start justify-between gap-3">
              <h3 className="text-sm font-semibold text-ink">
                {meta?.display_name ?? operation.process_type}
              </h3>
              <div className="flex items-center gap-3">
                {operation.unit_price_czk !== undefined && (
                  <span
                    className="text-xs text-ink-3"
                    data-testid={`path-step-price-${operation.id}`}
                  >
                    +{operation.unit_price_czk} Kč/ks
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => removeStep(operation.id)}
                  className="text-xs text-ink-3 hover:text-ink"
                  data-testid={`path-step-remove-${operation.id}`}
                >
                  {t("config.removeOperation", { defaultValue: "Odebrat" })}
                </button>
              </div>
            </header>
            {/* Karta se vykreslí, i když proces kroku vlastní konfigurátor
                nemá. Schovat v tom případě celý řádek — jak to dělala
                předchozí implementace — nechávalo krok oceněný a
                persistovaný, ale neviditelný a bez možnosti ho odebrat. */}
            {Configurator && (
              <Configurator
                config={config}
                analysis={null}
                lang={lang}
                onChange={(next: Record<string, unknown>) =>
                  updateStepConfig(operation.id, next)
                }
                {...dynamicSchemaProps}
              />
            )}
          </section>
        );
      })}

      {showAddCta && (
        <OperationPathPicker
          candidates={continuations.data ?? []}
          pathSoFar={pathSoFar}
          defaultConfig={cascadeConfig}
          isAdding={persistence?.addOperation?.isPending}
          onAddStep={
            persistence?.addOperation
              ? persistence.addOperation.mutateAsync
              : cartAddOp.mutateAsync
          }
          onStepAdded={onStepAdded}
          formatError={formatAddStepError}
          labels={{
            cta: t("config.addPathStepCta", {
              defaultValue: "+ Další krok výroby",
            }),
            pathSoFar: t("config.pathSoFar", { defaultValue: "Cesta zatím" }),
            chooseNext: t("config.choosePathStep", {
              defaultValue: "Pokračovat na",
            }),
          }}
        />
      )}
    </div>
  );
}

export default OperationPathSection;
